import {
  ActionIcon,
  Button,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Plus, X as XIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './page.module.css';

import { pointsApi, type PointNoteDetail } from '@fields/data-access';
import { FullMainHeight } from '@ui';

type LocalImage = {
  id: string;
  previewUrl: string;
  file: File;
};

export function PointNoteDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: '/map-note' });
  const search = useSearch({ from: '/_app/map-note' });
  const queryClient = useQueryClient();

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [removedImageIds, setRemovedImageIds] = useState<number[]>([]);
  const [localImages, setLocalImages] = useState<LocalImage[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const trimmedTitle = title.trim();
  const trimmedContent = content.trim();
  const titleError = submitAttempted && !trimmedTitle;
  const contentError = submitAttempted && !trimmedContent;
  const isFormValid = Boolean(trimmedTitle && trimmedContent);

  const noteQuery = useQuery({
    queryKey: ['points', search.pointId, 'notes', search.noteId, 'detail'],
    queryFn: () => pointsApi.getPointNoteDetail(search.pointId, search.noteId),
  });

  useEffect(() => {
    if (!noteQuery.data) return;
    setTitle(noteQuery.data.title);
    setContent(noteQuery.data.content);
  }, [noteQuery.data]);

  const updateNoteMutation = useMutation({
    mutationFn: (data: { title: string; content: string }) =>
      pointsApi.patchPointNote(search.pointId, search.noteId, {
        ...data,
        images: localImages.map((image) => image.file),
        remove_image_ids: removedImageIds,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['points', search.pointId, 'notes'],
      });
      void queryClient.invalidateQueries({
        queryKey: ['points', search.pointId, 'notes', search.noteId, 'detail'],
      });
      navigate({
        to: '/map-notes',
        search: {
          ilotId: search.ilotId,
          pointId: search.pointId,
          pointCode: search.pointCode,
          sampleId: search.sampleId,
          lng: search.lng,
          lat: search.lat,
          zoom: search.zoom,
        scope: search.scope,
        },
      });
    },
  });
  const isDetailLoading = noteQuery.isLoading || noteQuery.isFetching;
  const canSave = isFormValid && !isDetailLoading && !updateNoteMutation.isPending;

  const existingImages = useMemo(
    () =>
      (noteQuery.data?.images ?? []).filter(
        (img) => !removedImageIds.includes(img.id),
      ),
    [noteQuery.data?.images, removedImageIds],
  );

  const getBackendFileUrl = (maybePath: string | null) => {
    if (!maybePath) return null;
    if (maybePath.startsWith('http://') || maybePath.startsWith('https://')) {
      return maybePath;
    }

    const apiBase = import.meta.env.VITE_API_URL;
    const backendBase = apiBase.replace(/\/api\/?$/, '');
    const normalized = maybePath.startsWith('/') ? maybePath : `/${maybePath}`;
    return `${backendBase}${normalized}`;
  };

  const handleCancel = () => {
    navigate({
      to: '/map-notes',
      search: {
        ilotId: search.ilotId,
        pointId: search.pointId,
        pointCode: search.pointCode,
        sampleId: search.sampleId,
        lng: search.lng,
        lat: search.lat,
        zoom: search.zoom,
        scope: search.scope,
      },
    });
  };

  const handlePickImage = () => {
    fileInputRef.current?.click();
  };

  const handleAddImage = (e: { currentTarget: HTMLInputElement }) => {
    const files = Array.from(e.currentTarget.files ?? []);
    if (!files.length) return;
    const next = files.map((file) => ({
      id: `${file.name}-${file.lastModified}-${Math.random().toString(16).slice(2)}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setLocalImages((prev) => [...prev, ...next]);
    e.currentTarget.value = '';
  };

  const handleRemoveExistingImage = (imageId: number) => {
    setRemovedImageIds((prev) => [...prev, imageId]);
  };

  const handleRemoveLocalImage = (id: string) => {
    setLocalImages((prev) => {
      const image = prev.find((img) => img.id === id);
      if (image) URL.revokeObjectURL(image.previewUrl);
      return prev.filter((img) => img.id !== id);
    });
  };

  useEffect(() => {
    return () => {
      localImages.forEach((img) => URL.revokeObjectURL(img.previewUrl));
    };
  }, [localImages]);

  return (
    <FullMainHeight.Root>
      <FullMainHeight.Content className={classes.content}>
        <Stack gap={0} h="100%">
          <header className={classes.header}>
            <Text className={classes.headerTitle}>{t('sampling.notes.title')}</Text>
            <ActionIcon
              aria-label={t('sampling.notes.closeDetailAria')}
              color="dark"
              onClick={handleCancel}
              radius="xl"
              size={32}
              variant="light"
            >
              <XIcon size={18} />
            </ActionIcon>
          </header>

          <ScrollArea h="100%">
            <Stack className={classes.stack} gap="md">
              <Stack gap="sm">
                  <TextInput
                    fw={700}
                    variant="unstyled"
                    styles={{
                      root: {
                        padding: 0,
                        border: 'none',
                        background: 'transparent',
                      },
                      input: {
                        fontWeight: 700,
                        padding: 0,
                        border: 'none',
                        borderRadius: 0,
                        background: 'transparent',
                        fontSize: '1.125rem',
                        lineHeight: 1.2,
                        color: '#111827',
                      },
                    }}
                    onChange={(e) => setTitle(e.currentTarget.value)}
                    placeholder={t('sampling.notes.titlePlaceholder')}
                    value={title}
                  />
                  <span className={classes.greenLine} />
                  {titleError ? (
                    <Text c="red" size="xs">
                      {t('sampling.notes.titleRequired')}
                    </Text>
                  ) : null}

                  <Textarea
                    autosize
                    className={classes.contentInput}
                    minRows={5}
                    onChange={(e) => setContent(e.currentTarget.value)}
                    placeholder={t('sampling.notes.commentPlaceholder')}
                    value={content}
                  />
                  {contentError ? (
                    <Text c="red" size="xs">
                      {t('sampling.notes.commentRequired')}
                    </Text>
                  ) : null}

                  <span className={classes.greenLine} />

                  <div className={classes.mediaRow}>
                    {existingImages.map((image) => {
                      const src = getBackendFileUrl(image.image);
                      return (
                        <div className={classes.thumbWrap} key={image.id}>
                          {src ? (
                            <img
                              alt={t('sampling.notes.noteImageAlt', { id: image.id })}
                              className={classes.thumbImage}
                              src={src}
                            />
                          ) : null}
                          <ActionIcon
                            aria-label={t('sampling.notes.deletePhotoAria')}
                            className={classes.thumbDelete}
                            onClick={() => handleRemoveExistingImage(image.id)}
                            radius="xl"
                            size={22}
                            variant="filled"
                          >
                            <XIcon size={12} />
                          </ActionIcon>
                        </div>
                      );
                    })}

                    {localImages.map((image) => (
                      <div className={classes.thumbWrap} key={image.id}>
                        <img
                          alt={t('sampling.notes.newImageAlt')}
                          className={classes.thumbImage}
                          src={image.previewUrl}
                        />
                        <ActionIcon
                          aria-label={t('sampling.notes.removePhotoAria')}
                          className={classes.thumbDelete}
                          onClick={() => handleRemoveLocalImage(image.id)}
                          radius="xl"
                          size={22}
                          variant="filled"
                        >
                          <XIcon size={12} />
                        </ActionIcon>
                      </div>
                    ))}

                    <button
                      className={classes.addPhotoTile}
                      onClick={handlePickImage}
                      type="button"
                    >
                      <Plus size={22} />
                      <span>{t('sampling.notes.takePhoto')}</span>
                    </button>
                    <input
                      accept="image/*"
                      capture="environment"
                      onChange={handleAddImage}
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                      type="file"
                    />
                  </div>
                </Stack>
            </Stack>
          </ScrollArea>

          <div className={classes.footerSticky}>
            <div className={classes.footerActions}>
              <Button className={classes.cancelButton} onClick={handleCancel} radius="lg">
                {t('common.cancel')}
              </Button>
              <Button
                className={classes.saveButton}
                disabled={!canSave}
                loading={updateNoteMutation.isPending}
                onClick={() => {
                  setSubmitAttempted(true);
                  if (!canSave) return;
                  updateNoteMutation.mutate({
                    title: trimmedTitle,
                    content: trimmedContent,
                  });
                }}
                radius="lg"
              >
                {t('common.save')}
              </Button>
            </div>
          </div>
        </Stack>
      </FullMainHeight.Content>
    </FullMainHeight.Root>
  );
}

export default PointNoteDetailPage;
